import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { formatCurrency, formatNumber, formatDate, exportToPDF, exportToExcel } from '../utils/helpers';
import { 
  Plus, Users, Clock, TrendingUp, AlertCircle, FileText, FileSpreadsheet, 
  Edit, X, ChevronLeft, ChevronRight, Mail, Phone, Briefcase, Euro, Calendar,
  Trash2, User, Check, CalendarOff, CalendarCheck, Coffee, Sparkles, Zap, UserCheck, AlertTriangle,
  Book, BookOpen, Eye, EyeOff, Link, Copy, Settings, GripVertical, Save, Globe, Lock, Palette
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const PersoneelPlanningPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [workHours, setWorkHours] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [hoursDialog, setHoursDialog] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Employee detail/edit states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetailDialog, setEmployeeDetailDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Availability states
  const [availabilityData, setAvailabilityData] = useState({});
  const [timeOffData, setTimeOffData] = useState({});
  const [selectedAvailEmployee, setSelectedAvailEmployee] = useState(null);
  const [availabilityDialog, setAvailabilityDialog] = useState(false);
  const [timeOffDialog, setTimeOffDialog] = useState(false);
  const [timeOffForm, setTimeOffForm] = useState({ employee_id: '', date: '', reason: '' });
  const [dailyAvailability, setDailyAvailability] = useState({});
  
  // Handbook states
  const [handbookSettings, setHandbookSettings] = useState(null);
  const [handbookSections, setHandbookSections] = useState([]);
  const [handbookPages, setHandbookPages] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [handbookSettingsDialog, setHandbookSettingsDialog] = useState(false);
  const [sectionDialog, setSectionDialog] = useState(false);
  const [pageDialog, setPageDialog] = useState(false);
  const [editingPage, setEditingPage] = useState(false);
  const [sectionForm, setSectionForm] = useState({ title: '', icon: 'book' });
  const [pageForm, setPageForm] = useState({ title: '', content: '' });
  const [settingsForm, setSettingsForm] = useState({
    title: 'Personeelshandboek',
    subtitle: 'Welkom bij ons team!',
    company_name: '',
    password: '',
    is_published: false,
    theme_color: '#C2410C'
  });
  
  // Calendar state
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday start
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  
  // Quick schedule dialog
  const [quickScheduleDialog, setQuickScheduleDialog] = useState(false);
  const [quickScheduleDate, setQuickScheduleDate] = useState(null);
  
  // Optimization states
  const [optimizationDialog, setOptimizationDialog] = useState(false);
  const [optimizationLoading, setOptimizationLoading] = useState(false);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState(null);
  const [optimizationSettings, setOptimizationSettings] = useState({ minStaff: 2, maxStaff: 5 });
  const [selectedSuggestions, setSelectedSuggestions] = useState({});

  const [employeeForm, setEmployeeForm] = useState({
    name: '', email: '', phone: '', role: '', hourly_rate: ''
  });

  const [hoursForm, setHoursForm] = useState({
    employee_id: '', date: new Date().toISOString().split('T')[0], hours_worked: '', overtime: '', sick_leave: false, location_id: ''
  });

  const [scheduleForm, setScheduleForm] = useState({
    employee_id: '', date: new Date().toISOString().split('T')[0], shift_start: '', shift_end: '', location_id: ''
  });

  const dayNames = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
  const shortDayNames = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      fetchAllAvailability();
    }
  }, [employees]);

  useEffect(() => {
    if (activeTab === 'handbook') {
      fetchHandbookData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);
      const params = { start_date: startDate.toISOString().split('T')[0], end_date: endDate.toISOString().split('T')[0] };

      const [empRes, hoursRes, schedRes, revRes, locRes] = await Promise.all([
        api.get('/employees'),
        api.get('/work-hours', { params }),
        api.get('/schedules', { params }),
        api.get('/revenue', { params }),
        api.get('/locations')
      ]);
      setEmployees(empRes.data);
      setWorkHours(hoursRes.data);
      setSchedules(schedRes.data);
      setRevenue(revRes.data);
      setLocations(locRes.data);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  // Handbook functions
  const fetchHandbookData = async () => {
    try {
      const [settingsRes, sectionsRes, pagesRes] = await Promise.all([
        api.get('/handbook/settings'),
        api.get('/handbook/sections'),
        api.get('/handbook/pages')
      ]);
      setHandbookSettings(settingsRes.data);
      setHandbookSections(sectionsRes.data);
      setHandbookPages(pagesRes.data);
      setSettingsForm({
        title: settingsRes.data.title || 'Personeelshandboek',
        subtitle: settingsRes.data.subtitle || '',
        company_name: settingsRes.data.company_name || '',
        password: settingsRes.data.password || '',
        is_published: settingsRes.data.is_published || false,
        theme_color: settingsRes.data.theme_color || '#C2410C'
      });
    } catch (error) {
      console.error('Error fetching handbook:', error);
    }
  };

  const handleSaveHandbookSettings = async () => {
    try {
      await api.put('/handbook/settings', settingsForm);
      toast.success('Instellingen opgeslagen');
      setHandbookSettingsDialog(false);
      fetchHandbookData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleCreateSection = async () => {
    try {
      await api.post('/handbook/sections', sectionForm);
      toast.success('Sectie aangemaakt');
      setSectionDialog(false);
      setSectionForm({ title: '', icon: 'book' });
      fetchHandbookData();
    } catch (error) {
      toast.error('Fout bij aanmaken');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Sectie en alle pagina\'s verwijderen?')) return;
    try {
      await api.delete(`/handbook/sections/${sectionId}`);
      toast.success('Sectie verwijderd');
      if (selectedSection?.id === sectionId) {
        setSelectedSection(null);
        setSelectedPage(null);
      }
      fetchHandbookData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleCreatePage = async () => {
    if (!selectedSection) return;
    try {
      await api.post('/handbook/pages', {
        section_id: selectedSection.id,
        title: pageForm.title || 'Nieuwe pagina',
        content: pageForm.content || ''
      });
      toast.success('Pagina aangemaakt');
      setPageDialog(false);
      setPageForm({ title: '', content: '' });
      fetchHandbookData();
    } catch (error) {
      toast.error('Fout bij aanmaken');
    }
  };

  const handleSavePage = async () => {
    if (!selectedPage) return;
    try {
      await api.put(`/handbook/pages/${selectedPage.id}`, {
        title: pageForm.title,
        content: pageForm.content
      });
      toast.success('Pagina opgeslagen');
      setEditingPage(false);
      fetchHandbookData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Pagina verwijderen?')) return;
    try {
      await api.delete(`/handbook/pages/${pageId}`);
      toast.success('Pagina verwijderd');
      if (selectedPage?.id === pageId) {
        setSelectedPage(null);
      }
      fetchHandbookData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const selectPage = (page) => {
    setSelectedPage(page);
    setPageForm({ title: page.title, content: page.content });
    setEditingPage(false);
  };

  const copyHandbookLink = () => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/handboek`;
    navigator.clipboard.writeText(link);
    toast.success('Link gekopieerd!');
  };

  const fetchAllAvailability = async () => {
    try {
      const availPromises = employees.map(emp => 
        api.get(`/availability/${emp.id}`).catch(() => ({ data: [] }))
      );
      const timeOffPromises = employees.map(emp => 
        api.get(`/time-off/${emp.id}`).catch(() => ({ data: [] }))
      );
      
      const availResults = await Promise.all(availPromises);
      const timeOffResults = await Promise.all(timeOffPromises);
      
      const availMap = {};
      const timeOffMap = {};
      
      employees.forEach((emp, i) => {
        availMap[emp.id] = availResults[i].data;
        timeOffMap[emp.id] = timeOffResults[i].data;
      });
      
      setAvailabilityData(availMap);
      setTimeOffData(timeOffMap);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchDailyAvailability = async (dateStr) => {
    try {
      const res = await api.get('/availability/check', { params: { date: dateStr } });
      const map = {};
      res.data.forEach(item => {
        map[item.employee_id] = item;
      });
      setDailyAvailability(prev => ({ ...prev, [dateStr]: map }));
    } catch (error) {
      console.error('Error fetching daily availability:', error);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await api.post('/employees', {
        ...employeeForm,
        hourly_rate: parseFloat(employeeForm.hourly_rate) || 0
      });
      toast.success('Medewerker toegevoegd');
      setEmployeeDialog(false);
      setEmployeeForm({ name: '', email: '', phone: '', role: '', hourly_rate: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/employees/${selectedEmployee.id}`, {
        ...editForm,
        hourly_rate: parseFloat(editForm.hourly_rate) || 0
      });
      toast.success('Medewerker bijgewerkt');
      setEditMode(false);
      setEmployeeDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleAddHours = async (e) => {
    e.preventDefault();
    try {
      await api.post('/work-hours', {
        ...hoursForm,
        hours_worked: parseFloat(hoursForm.hours_worked) || 0,
        overtime: parseFloat(hoursForm.overtime) || 0,
        location_id: hoursForm.location_id || null
      });
      toast.success('Uren geregistreerd');
      setHoursDialog(false);
      setHoursForm({ employee_id: '', date: new Date().toISOString().split('T')[0], hours_worked: '', overtime: '', sick_leave: false, location_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    try {
      await api.post('/schedules', null, {
        params: {
          employee_id: scheduleForm.employee_id,
          date: scheduleForm.date,
          shift_start: scheduleForm.shift_start,
          shift_end: scheduleForm.shift_end,
          location_id: scheduleForm.location_id || null
        }
      });
      toast.success('Dienst ingepland');
      setScheduleDialog(false);
      setQuickScheduleDialog(false);
      setScheduleForm({ employee_id: '', date: new Date().toISOString().split('T')[0], shift_start: '', shift_end: '', location_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleSaveAvailability = async () => {
    if (!selectedAvailEmployee) return;
    
    try {
      const availability = dayNames.map((_, idx) => {
        const existing = (availabilityData[selectedAvailEmployee.id] || []).find(a => a.day_of_week === idx);
        return {
          day_of_week: idx,
          is_available: existing?.is_available ?? true,
          start_time: existing?.start_time || '09:00',
          end_time: existing?.end_time || '17:00',
          notes: existing?.notes || ''
        };
      });
      
      await api.post('/availability/bulk', availability, {
        params: { employee_id: selectedAvailEmployee.id }
      });
      
      toast.success('Beschikbaarheid opgeslagen');
      setAvailabilityDialog(false);
      fetchAllAvailability();
    } catch (error) {
      toast.error('Fout bij opslaan beschikbaarheid');
    }
  };

  const handleAddTimeOff = async (e) => {
    e.preventDefault();
    try {
      await api.post('/time-off', null, {
        params: {
          employee_id: timeOffForm.employee_id,
          date: timeOffForm.date,
          reason: timeOffForm.reason
        }
      });
      toast.success('Vrije dag toegevoegd');
      setTimeOffDialog(false);
      setTimeOffForm({ employee_id: '', date: '', reason: '' });
      fetchAllAvailability();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDeleteTimeOff = async (timeOffId) => {
    try {
      await api.delete(`/time-off/${timeOffId}`);
      toast.success('Vrije dag verwijderd');
      fetchAllAvailability();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Medewerker verwijderen?')) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Medewerker verwijderd');
      setEmployeeDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Dienst verwijderen?')) return;
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success('Dienst verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleOptimize = async () => {
    setOptimizationLoading(true);
    try {
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + 6);
      
      const res = await api.get('/schedule/optimize', {
        params: {
          start_date: currentWeekStart.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          min_staff: optimizationSettings.minStaff,
          max_staff: optimizationSettings.maxStaff
        }
      });
      
      setOptimizationSuggestions(res.data);
      
      // Pre-select all suggestions by default
      const selections = {};
      res.data.suggestions.forEach(day => {
        selections[day.date] = day.suggested_employees.map(e => ({
          ...e,
          selected: true
        }));
      });
      setSelectedSuggestions(selections);
      
      setOptimizationDialog(true);
    } catch (error) {
      toast.error('Fout bij optimaliseren');
    } finally {
      setOptimizationLoading(false);
    }
  };

  const toggleSuggestionEmployee = (date, employeeId) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [date]: prev[date].map(emp => 
        emp.id === employeeId ? { ...emp, selected: !emp.selected } : emp
      )
    }));
  };

  const handleApplySelectedSuggestions = async () => {
    try {
      let createdCount = 0;
      
      for (const [date, employees] of Object.entries(selectedSuggestions)) {
        const selectedEmps = employees.filter(e => e.selected);
        
        for (const emp of selectedEmps) {
          await api.post('/schedules', null, {
            params: {
              employee_id: emp.id,
              date: date,
              shift_start: emp.suggested_start || '09:00',
              shift_end: emp.suggested_end || '17:00'
            }
          });
          createdCount++;
        }
      }
      
      toast.success(`${createdCount} diensten ingepland!`);
      setOptimizationDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij toepassen suggesties');
    }
  };

  const openEmployeeDetail = (emp) => {
    setSelectedEmployee(emp);
    setEditForm({
      name: emp.name,
      email: emp.email || '',
      phone: emp.phone || '',
      role: emp.role || '',
      hourly_rate: emp.hourly_rate || '',
      is_active: emp.is_active
    });
    setEditMode(false);
    setEmployeeDetailDialog(true);
  };

  const openAvailabilityDialog = (emp) => {
    setSelectedAvailEmployee(emp);
    setAvailabilityDialog(true);
  };

  const updateAvailabilityForDay = (dayIdx, field, value) => {
    setAvailabilityData(prev => {
      const empData = [...(prev[selectedAvailEmployee.id] || [])];
      const existing = empData.find(a => a.day_of_week === dayIdx);
      
      if (existing) {
        const idx = empData.indexOf(existing);
        empData[idx] = { ...existing, [field]: value };
      } else {
        empData.push({
          day_of_week: dayIdx,
          is_available: true,
          start_time: '09:00',
          end_time: '17:00',
          notes: '',
          [field]: value
        });
      }
      
      return { ...prev, [selectedAvailEmployee.id]: empData };
    });
  };

  const openQuickSchedule = (date) => {
    setQuickScheduleDate(date);
    setScheduleForm({
      ...scheduleForm,
      date: date.toISOString().split('T')[0],
      shift_start: '09:00',
      shift_end: '17:00'
    });
    
    // Fetch availability for this date
    fetchDailyAvailability(date.toISOString().split('T')[0]);
    setQuickScheduleDialog(true);
  };

  // Calendar navigation
  const goToPreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setCurrentWeekStart(monday);
  };

  // Get week days
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push({
        date: day,
        name: shortDayNames[i],
        dateStr: day.toISOString().split('T')[0],
        dayNum: day.getDate(),
        monthName: day.toLocaleDateString('nl-NL', { month: 'short' }),
        isToday: day.toDateString() === new Date().toDateString(),
        dayOfWeek: i
      });
    }
    return days;
  };

  const weekDays = getWeekDays();

  // Get schedules for a specific date
  const getSchedulesForDate = (dateStr) => {
    return schedules.filter(s => s.date === dateStr).map(s => ({
      ...s,
      employee: employees.find(e => e.id === s.employee_id)
    }));
  };

  // Get availability status for employee on date
  const getEmployeeStatus = (employeeId, dateStr, dayOfWeek) => {
    // Check time off first
    const empTimeOff = timeOffData[employeeId] || [];
    if (empTimeOff.find(t => t.date === dateStr)) {
      return 'vrij';
    }
    
    // Check weekly availability
    const empAvail = availabilityData[employeeId] || [];
    const dayAvail = empAvail.find(a => a.day_of_week === dayOfWeek);
    if (dayAvail && !dayAvail.is_available) {
      return 'niet_beschikbaar';
    }
    
    // Check if already scheduled
    const scheduled = schedules.find(s => s.date === dateStr && s.employee_id === employeeId);
    if (scheduled) {
      return 'ingepland';
    }
    
    return 'beschikbaar';
  };

  // Calculate metrics
  const totalHours = workHours.reduce((sum, h) => sum + (h.hours_worked || 0) + (h.overtime || 0), 0);
  const totalOvertime = workHours.reduce((sum, h) => sum + (h.overtime || 0), 0);
  const sickDays = workHours.filter(h => h.sick_leave).length;
  const totalRevenue = revenue.reduce((sum, r) => sum + (r.amount || 0), 0);
  const revenuePerHour = totalHours > 0 ? totalRevenue / totalHours : 0;

  // Employee productivity data
  const employeeData = employees.map(emp => {
    const empHours = workHours.filter(h => h.employee_id === emp.id);
    const totalEmpHours = empHours.reduce((sum, h) => sum + (h.hours_worked || 0), 0);
    const empOvertime = empHours.reduce((sum, h) => sum + (h.overtime || 0), 0);
    const sickCount = empHours.filter(h => h.sick_leave).length;
    
    return {
      ...emp,
      total_hours: totalEmpHours,
      overtime: empOvertime,
      sick_days: sickCount,
      revenue_per_hour: totalEmpHours > 0 ? totalRevenue / totalEmpHours / employees.length : 0
    };
  });

  // Employee colors for calendar
  const employeeColors = [
    'bg-orange-100 border-orange-400 text-orange-800',
    'bg-emerald-100 border-emerald-400 text-emerald-800',
    'bg-blue-100 border-blue-400 text-blue-800',
    'bg-purple-100 border-purple-400 text-purple-800',
    'bg-pink-100 border-pink-400 text-pink-800',
    'bg-amber-100 border-amber-400 text-amber-800',
    'bg-teal-100 border-teal-400 text-teal-800',
    'bg-indigo-100 border-indigo-400 text-indigo-800',
  ];

  const getEmployeeColor = (employeeId) => {
    const index = employees.findIndex(e => e.id === employeeId);
    return employeeColors[index % employeeColors.length];
  };

  const statusColors = {
    beschikbaar: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    niet_beschikbaar: 'bg-stone-100 text-stone-500 border-stone-300',
    vrij: 'bg-amber-100 text-amber-700 border-amber-300',
    ingepland: 'bg-blue-100 text-blue-700 border-blue-300'
  };

  const statusIcons = {
    beschikbaar: <Check className="w-3 h-3" />,
    niet_beschikbaar: <X className="w-3 h-3" />,
    vrij: <Coffee className="w-3 h-3" />,
    ingepland: <Calendar className="w-3 h-3" />
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="personeel-planning-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Personeel & Planning
          </h1>
          <p className="text-[#57534E] mt-1">
            Beheer medewerkers, uren, beschikbaarheid en roosters
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportToPDF('Personeel',
            [{ header: 'Naam', key: 'name' }, { header: 'Rol', key: 'role' }, { header: 'Uren', key: 'total_hours' }],
            employeeData, 'personeel'
          )}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportToExcel('Personeel',
            [{ header: 'Naam', key: 'name' }, { header: 'Email', key: 'email' }, { header: 'Rol', key: 'role' }, { header: 'Uurloon', key: 'hourly_rate' }],
            employees, 'personeel'
          )}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="kpi-tile">
          <p className="kpi-label">Medewerkers</p>
          <p className="kpi-value">{employees.filter(e => e.is_active).length}</p>
          <p className="text-sm text-[#57534E]">actief</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Totaal Uren</p>
          <p className="kpi-value">{formatNumber(totalHours)}</p>
          <p className="text-sm text-[#57534E]">deze periode</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Overuren</p>
          <p className="kpi-value text-amber-600">{formatNumber(totalOvertime)}</p>
          <p className="text-sm text-[#57534E]">uren</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Ziektedagen</p>
          <p className={`kpi-value ${sickDays > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{sickDays}</p>
          <p className="text-sm text-[#57534E]">registraties</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Omzet/Uur</p>
          <p className="kpi-value text-emerald-600">{formatCurrency(revenuePerHour)}</p>
          <p className="text-sm text-[#57534E]">per medewerker</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="employees">Medewerkers</TabsTrigger>
          <TabsTrigger value="availability">Beschikbaarheid</TabsTrigger>
          <TabsTrigger value="hours">Uren</TabsTrigger>
          <TabsTrigger value="schedule">Rooster</TabsTrigger>
          <TabsTrigger value="handbook" data-testid="handbook-tab">
            <Book className="w-4 h-4 mr-1" />
            Handboek
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Uren per Medewerker</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={employeeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#57534E' }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_hours" name="Gewerkt" fill="#C2410C" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="overtime" name="Overuren" fill="#D97706" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Productiviteit</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {employeeData.map(emp => (
                    <div 
                      key={emp.id} 
                      className="flex items-center justify-between p-3 bg-stone-50 rounded-lg cursor-pointer hover:bg-stone-100 transition-colors"
                      onClick={() => openEmployeeDetail(emp)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#C2410C]/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-[#C2410C]" />
                        </div>
                        <div>
                          <p className="font-medium">{emp.name}</p>
                          <p className="text-sm text-[#57534E]">{emp.role}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{emp.total_hours}u</p>
                        <p className="text-sm text-emerald-600">{formatCurrency(emp.revenue_per_hour)}/u</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Medewerkers</CardTitle>
              <Dialog open={employeeDialog} onOpenChange={setEmployeeDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-employee-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Medewerker toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nieuwe medewerker</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddEmployee} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Naam</Label>
                      <Input
                        value={employeeForm.name}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={employeeForm.email}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefoon</Label>
                        <Input
                          value={employeeForm.phone}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Functie</Label>
                        <Input
                          value={employeeForm.role}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, role: e.target.value })}
                          placeholder="bijv. Kok, Bediening"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Uurloon (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={employeeForm.hourly_rate}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, hourly_rate: e.target.value })}
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {employees.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-[#57534E]">
                    Nog geen medewerkers
                  </div>
                ) : (
                  employees.map((emp) => (
                    <Card 
                      key={emp.id} 
                      className="card-base cursor-pointer hover:shadow-lg transition-all hover:border-[#C2410C]/30"
                      onClick={() => openEmployeeDetail(emp)}
                      data-testid={`employee-card-${emp.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C2410C] to-[#D97706] flex items-center justify-center text-white font-bold text-lg">
                            {emp.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-[#1C1917] truncate">{emp.name}</h3>
                              <Badge className={emp.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'}>
                                {emp.is_active ? 'Actief' : 'Inactief'}
                              </Badge>
                            </div>
                            <p className="text-sm text-[#57534E] mt-1">{emp.role}</p>
                            <div className="flex items-center gap-4 mt-3 text-xs text-[#78716C]">
                              {emp.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {emp.email.split('@')[0]}...
                                </span>
                              )}
                              {emp.hourly_rate > 0 && (
                                <span className="flex items-center gap-1">
                                  <Euro className="w-3 h-3" />
                                  {emp.hourly_rate}/u
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Availability Tab */}
        <TabsContent value="availability" className="mt-6 space-y-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Wekelijkse Beschikbaarheid</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#57534E] mb-4">
                Stel per medewerker in op welke dagen en tijden ze beschikbaar zijn.
              </p>
              <div className="space-y-4">
                {employees.filter(e => e.is_active).map(emp => {
                  const empAvail = availabilityData[emp.id] || [];
                  const availDays = dayNames.map((_, idx) => {
                    const dayData = empAvail.find(a => a.day_of_week === idx);
                    return dayData?.is_available !== false;
                  });
                  const availCount = availDays.filter(Boolean).length;
                  
                  return (
                    <div 
                      key={emp.id}
                      className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200 hover:border-[#C2410C]/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C2410C] to-[#D97706] flex items-center justify-center text-white font-bold">
                          {emp.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold">{emp.name}</p>
                          <p className="text-sm text-[#57534E]">{emp.role}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        {/* Week availability mini display */}
                        <div className="hidden md:flex items-center gap-1">
                          {shortDayNames.map((day, idx) => (
                            <div
                              key={day}
                              className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium ${
                                availDays[idx] 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-stone-200 text-stone-400'
                              }`}
                            >
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        <Badge className={availCount >= 5 ? 'bg-emerald-100 text-emerald-700' : availCount >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}>
                          {availCount}/7 dagen
                        </Badge>
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openAvailabilityDialog(emp)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Bewerken
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Time Off Section */}
          <Card className="card-base border-amber-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <CalendarOff className="w-5 h-5 text-amber-600" />
                Vrije Dagen & Vakantie
              </CardTitle>
              <Dialog open={timeOffDialog} onOpenChange={setTimeOffDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Vrije dag toevoegen
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Vrije dag toevoegen</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddTimeOff} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Medewerker</Label>
                      <Select
                        value={timeOffForm.employee_id}
                        onValueChange={(v) => setTimeOffForm({ ...timeOffForm, employee_id: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer medewerker" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.filter(e => e.is_active).map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Datum</Label>
                      <Input
                        type="date"
                        value={timeOffForm.date}
                        onChange={(e) => setTimeOffForm({ ...timeOffForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Reden</Label>
                      <Select
                        value={timeOffForm.reason}
                        onValueChange={(v) => setTimeOffForm({ ...timeOffForm, reason: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer reden" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vakantie">Vakantie</SelectItem>
                          <SelectItem value="ziek">Ziek</SelectItem>
                          <SelectItem value="vrij">Vrij</SelectItem>
                          <SelectItem value="feestdag">Feestdag</SelectItem>
                          <SelectItem value="overig">Overig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                      Toevoegen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {Object.values(timeOffData).flat().length === 0 ? (
                <p className="text-center py-8 text-[#57534E]">Geen vrije dagen gepland</p>
              ) : (
                <div className="space-y-3">
                  {employees.filter(e => e.is_active).map(emp => {
                    const empTimeOff = timeOffData[emp.id] || [];
                    if (empTimeOff.length === 0) return null;
                    
                    return (
                      <div key={emp.id} className="p-3 bg-amber-50 rounded-lg">
                        <p className="font-semibold mb-2">{emp.name}</p>
                        <div className="flex flex-wrap gap-2">
                          {empTimeOff.sort((a, b) => a.date.localeCompare(b.date)).map(to => (
                            <div 
                              key={to.id}
                              className="flex items-center gap-2 px-3 py-1 bg-white rounded-full border border-amber-200"
                            >
                              <span className="text-sm">
                                {new Date(to.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                              </span>
                              <Badge variant="outline" className="text-xs">{to.reason || 'vrij'}</Badge>
                              <button 
                                onClick={() => handleDeleteTimeOff(to.id)}
                                className="text-rose-500 hover:text-rose-700"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hours Tab */}
        <TabsContent value="hours" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Gewerkte Uren</CardTitle>
              <Dialog open={hoursDialog} onOpenChange={setHoursDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-hours-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Uren registreren
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Uren registreren</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddHours} className="space-y-4 pt-4">
                    <div className="space-y-2">
                      <Label>Medewerker</Label>
                      <Select
                        value={hoursForm.employee_id}
                        onValueChange={(value) => setHoursForm({ ...hoursForm, employee_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer medewerker" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Datum</Label>
                      <Input
                        type="date"
                        value={hoursForm.date}
                        onChange={(e) => setHoursForm({ ...hoursForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Gewerkte uren</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={hoursForm.hours_worked}
                          onChange={(e) => setHoursForm({ ...hoursForm, hours_worked: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Overuren</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={hoursForm.overtime}
                          onChange={(e) => setHoursForm({ ...hoursForm, overtime: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={hoursForm.sick_leave}
                        onCheckedChange={(checked) => setHoursForm({ ...hoursForm, sick_leave: checked })}
                      />
                      <Label>Ziekmelding</Label>
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Registreren
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Medewerker</TableHead>
                    <TableHead>Gewerkt</TableHead>
                    <TableHead>Overuren</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workHours.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#57534E]">
                        Nog geen uren geregistreerd
                      </TableCell>
                    </TableRow>
                  ) : (
                    workHours.sort((a, b) => new Date(b.date) - new Date(a.date)).map((h) => {
                      const emp = employees.find(e => e.id === h.employee_id);
                      return (
                        <TableRow key={h.id} className="table-row">
                          <TableCell>{formatDate(h.date)}</TableCell>
                          <TableCell className="font-medium">{emp?.name || 'Onbekend'}</TableCell>
                          <TableCell>{h.hours_worked}u</TableCell>
                          <TableCell className={h.overtime > 0 ? 'text-amber-600' : ''}>
                            {h.overtime > 0 ? `+${h.overtime}u` : '-'}
                          </TableCell>
                          <TableCell>
                            {h.sick_leave ? (
                              <Badge className="bg-rose-100 text-rose-700">Ziek</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">Gewerkt</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Tab - Calendar View */}
        <TabsContent value="schedule" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <CardTitle style={{ fontFamily: 'Manrope' }}>Weekrooster</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={goToPreviousWeek}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
                    Vandaag
                  </Button>
                  <Button variant="outline" size="sm" onClick={goToNextWeek}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <span className="text-sm text-[#57534E]">
                  {currentWeekStart.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })} - {
                    new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
                  }
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleOptimize}
                  disabled={optimizationLoading}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  data-testid="optimize-schedule-btn"
                >
                  {optimizationLoading ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
                      Bezig...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Optimaliseer
                    </>
                  )}
                </Button>
                <Dialog open={scheduleDialog} onOpenChange={setScheduleDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" data-testid="add-schedule-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Dienst inplannen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Dienst inplannen</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddSchedule} className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Medewerker</Label>
                        <Select
                          value={scheduleForm.employee_id}
                          onValueChange={(value) => setScheduleForm({ ...scheduleForm, employee_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer medewerker" />
                          </SelectTrigger>
                          <SelectContent>
                            {employees.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Datum</Label>
                        <Input
                          type="date"
                          value={scheduleForm.date}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, date: e.target.value })}
                          required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start</Label>
                        <Input
                          type="time"
                          value={scheduleForm.shift_start}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, shift_start: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Eind</Label>
                        <Input
                          type="time"
                          value={scheduleForm.shift_end}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, shift_end: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Locatie (optioneel)</Label>
                      <Select
                        value={scheduleForm.location_id || "none"}
                        onValueChange={(value) => setScheduleForm({ ...scheduleForm, location_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer locatie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Geen</SelectItem>
                          {locations.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full btn-primary">
                      Inplannen
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {/* Day Headers */}
                {weekDays.map((day) => (
                  <div 
                    key={day.dateStr}
                    className={`text-center p-2 rounded-t-lg ${day.isToday ? 'bg-[#C2410C] text-white' : 'bg-stone-100'}`}
                  >
                    <p className="text-sm font-semibold">{day.name}</p>
                    <p className={`text-lg font-bold ${day.isToday ? '' : 'text-[#1C1917]'}`}>{day.dayNum}</p>
                    <p className="text-xs">{day.monthName}</p>
                  </div>
                ))}
                
                {/* Day Columns */}
                {weekDays.map((day) => {
                  const daySchedules = getSchedulesForDate(day.dateStr);
                  return (
                    <div 
                      key={`col-${day.dateStr}`}
                      className={`min-h-[200px] border rounded-b-lg p-2 ${day.isToday ? 'border-[#C2410C] bg-[#C2410C]/5' : 'border-stone-200 bg-white'}`}
                    >
                      <div className="space-y-2">
                        {daySchedules.map((schedule) => {
                          const status = getEmployeeStatus(schedule.employee_id, day.dateStr, day.dayOfWeek);
                          return (
                            <div 
                              key={schedule.id}
                              className={`p-2 rounded-lg border-l-4 text-xs cursor-pointer hover:shadow-md transition-shadow ${getEmployeeColor(schedule.employee_id)}`}
                              onClick={() => {
                                if (window.confirm(`Dienst van ${schedule.employee?.name || 'Onbekend'} verwijderen?`)) {
                                  handleDeleteSchedule(schedule.id);
                                }
                              }}
                            >
                              <p className="font-semibold truncate">{schedule.employee?.name || 'Onbekend'}</p>
                              <p className="text-[10px] mt-1">
                                {schedule.shift_start} - {schedule.shift_end}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Add shift button */}
                      <button
                        onClick={() => openQuickSchedule(day.date)}
                        className="w-full mt-2 p-2 border-2 border-dashed border-stone-300 rounded-lg text-stone-400 hover:border-[#C2410C] hover:text-[#C2410C] transition-colors flex items-center justify-center gap-1 text-xs"
                      >
                        <Plus className="w-3 h-3" />
                        Inplannen
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Employee Legend with availability status */}
              <div className="mt-6 p-4 bg-stone-50 rounded-lg">
                <p className="text-sm font-semibold text-[#1C1917] mb-3">Medewerkers</p>
                <div className="flex flex-wrap gap-2">
                  {employees.filter(e => e.is_active).map((emp, index) => (
                    <div 
                      key={emp.id}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${employeeColors[index % employeeColors.length]}`}
                    >
                      {emp.name}
                    </div>
                  ))}
                </div>
                
                {/* Status legend */}
                <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-stone-200">
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-4 h-4 rounded bg-emerald-100 border border-emerald-300 flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-emerald-600" />
                    </div>
                    <span>Beschikbaar</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-4 h-4 rounded bg-amber-100 border border-amber-300 flex items-center justify-center">
                      <Coffee className="w-2.5 h-2.5 text-amber-600" />
                    </div>
                    <span>Vrij</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-4 h-4 rounded bg-stone-100 border border-stone-300 flex items-center justify-center">
                      <X className="w-2.5 h-2.5 text-stone-400" />
                    </div>
                    <span>Niet beschikbaar</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-4 h-4 rounded bg-blue-100 border border-blue-300 flex items-center justify-center">
                      <Calendar className="w-2.5 h-2.5 text-blue-600" />
                    </div>
                    <span>Ingepland</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Handbook Tab */}
        <TabsContent value="handbook" className="mt-6" data-testid="handbook-content">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar - Sections */}
            <div className="lg:col-span-1 space-y-4">
              <Card className="card-base">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Secties</CardTitle>
                    <Button size="sm" variant="outline" onClick={() => setSectionDialog(true)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {handbookSections.length === 0 ? (
                    <div className="text-center py-4 text-[#57534E] text-sm">
                      <Book className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                      <p>Nog geen secties</p>
                      <Button size="sm" variant="link" onClick={() => setSectionDialog(true)}>
                        Eerste sectie toevoegen
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {handbookSections.map(section => (
                        <div 
                          key={section.id}
                          className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                            selectedSection?.id === section.id 
                              ? 'bg-[#C2410C]/10 border border-[#C2410C]/30' 
                              : 'hover:bg-stone-50'
                          }`}
                          onClick={() => { setSelectedSection(section); setSelectedPage(null); }}
                        >
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-[#57534E]" />
                            <span className="text-sm font-medium">{section.title}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Badge variant="secondary" className="text-xs">
                              {handbookPages.filter(p => p.section_id === section.id).length}
                            </Badge>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-500" onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pages in selected section */}
              {selectedSection && (
                <Card className="card-base">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Pagina's</CardTitle>
                      <Button size="sm" variant="outline" onClick={() => setPageDialog(true)}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {handbookPages.filter(p => p.section_id === selectedSection.id).length === 0 ? (
                      <div className="text-center py-4 text-[#57534E] text-sm">
                        <p>Geen pagina's</p>
                        <Button size="sm" variant="link" onClick={() => setPageDialog(true)}>
                          Pagina toevoegen
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {handbookPages.filter(p => p.section_id === selectedSection.id).map(page => (
                          <div 
                            key={page.id}
                            className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                              selectedPage?.id === page.id 
                                ? 'bg-[#C2410C]/10 border border-[#C2410C]/30' 
                                : 'hover:bg-stone-50'
                            }`}
                            onClick={() => selectPage(page)}
                          >
                            <span className="text-sm">{page.title}</span>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-rose-500" onClick={(e) => { e.stopPropagation(); handleDeletePage(page.id); }}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <Card className="card-base">
                <CardContent className="p-4 space-y-2">
                  <Button variant="outline" className="w-full justify-start" onClick={() => setHandbookSettingsDialog(true)}>
                    <Settings className="w-4 h-4 mr-2" />
                    Instellingen
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={copyHandbookLink}>
                    <Copy className="w-4 h-4 mr-2" />
                    Link kopiëren
                  </Button>
                  <a href="/handboek" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="w-full justify-start">
                      <Eye className="w-4 h-4 mr-2" />
                      Bekijk handboek
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>

            {/* Main Content - Page Editor */}
            <div className="lg:col-span-3">
              <Card className="card-base">
                <CardContent className="p-6">
                  {!selectedSection && !selectedPage && (
                    <div className="text-center py-16 text-[#57534E]">
                      <Book className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                      <h3 className="text-xl font-semibold text-[#1C1917] mb-2">Personeelshandboek</h3>
                      <p className="mb-6 max-w-md mx-auto">
                        Maak een digitaal handboek voor je medewerkers. Voeg secties en pagina's toe met alle informatie die ze nodig hebben.
                      </p>
                      <div className="flex items-center justify-center gap-4">
                        <Button onClick={() => setSectionDialog(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Eerste sectie toevoegen
                        </Button>
                        <Button variant="outline" onClick={() => setHandbookSettingsDialog(true)}>
                          <Settings className="w-4 h-4 mr-2" />
                          Instellingen
                        </Button>
                      </div>
                      {handbookSettings && (
                        <div className="mt-8 p-4 bg-stone-50 rounded-lg max-w-md mx-auto">
                          <div className="flex items-center justify-between text-sm">
                            <span>Status:</span>
                            <Badge className={handbookSettings.is_published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {handbookSettings.is_published ? 'Gepubliceerd' : 'Concept'}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm mt-2">
                            <span>Wachtwoord:</span>
                            <span className="flex items-center gap-1">
                              {handbookSettings.password ? (
                                <><Lock className="w-4 h-4 text-emerald-600" /> Beveiligd</>
                              ) : (
                                <><Globe className="w-4 h-4 text-amber-600" /> Openbaar</>
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedSection && !selectedPage && (
                    <div className="text-center py-12 text-[#57534E]">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 text-stone-300" />
                      <h3 className="text-lg font-semibold text-[#1C1917] mb-2">{selectedSection.title}</h3>
                      <p className="mb-4">Selecteer een pagina of maak een nieuwe aan</p>
                      <Button onClick={() => setPageDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nieuwe pagina
                      </Button>
                    </div>
                  )}

                  {selectedPage && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b pb-4">
                        {editingPage ? (
                          <Input 
                            value={pageForm.title}
                            onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })}
                            className="text-xl font-bold max-w-md"
                          />
                        ) : (
                          <h2 className="text-xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
                            {selectedPage.title}
                          </h2>
                        )}
                        <div className="flex items-center gap-2">
                          {editingPage ? (
                            <>
                              <Button variant="outline" onClick={() => setEditingPage(false)}>Annuleren</Button>
                              <Button className="btn-primary" onClick={handleSavePage}>
                                <Save className="w-4 h-4 mr-2" />
                                Opslaan
                              </Button>
                            </>
                          ) : (
                            <Button variant="outline" onClick={() => setEditingPage(true)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Bewerken
                            </Button>
                          )}
                        </div>
                      </div>

                      {editingPage ? (
                        <div className="space-y-4">
                          <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                            <p className="font-medium">💡 Tips voor opmaak:</p>
                            <ul className="mt-1 ml-4 list-disc">
                              <li># Kop 1, ## Kop 2, ### Kop 3</li>
                              <li>**vetgedrukt**, *cursief*</li>
                              <li>- Opsommingslijst</li>
                              <li>1. Genummerde lijst</li>
                            </ul>
                          </div>
                          <Textarea
                            value={pageForm.content}
                            onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })}
                            placeholder="Schrijf de inhoud van deze pagina...

Je kunt Markdown gebruiken voor opmaak:
# Grote kop
## Kleinere kop

**Vetgedrukte tekst** en *schuine tekst*

- Item 1
- Item 2
- Item 3"
                            className="min-h-[400px] font-mono text-sm"
                          />
                        </div>
                      ) : (
                        <div className="prose prose-sm max-w-none">
                          {selectedPage.content ? (
                            <div className="whitespace-pre-wrap text-[#57534E] leading-relaxed">
                              {selectedPage.content.split('\n').map((line, i) => {
                                if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold text-[#1C1917] mt-6 mb-3">{line.slice(2)}</h1>;
                                if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-semibold text-[#1C1917] mt-5 mb-2">{line.slice(3)}</h2>;
                                if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-medium text-[#1C1917] mt-4 mb-2">{line.slice(4)}</h3>;
                                if (line.startsWith('- ')) return <li key={i} className="ml-4">{line.slice(2)}</li>;
                                if (line.match(/^\d+\. /)) return <li key={i} className="ml-4 list-decimal">{line.replace(/^\d+\. /, '')}</li>;
                                if (line.trim() === '') return <br key={i} />;
                                return <p key={i} className="my-2">{line}</p>;
                              })}
                            </div>
                          ) : (
                            <p className="text-[#57534E] italic">Deze pagina heeft nog geen inhoud. Klik op "Bewerken" om te beginnen.</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Section Dialog */}
          <Dialog open={sectionDialog} onOpenChange={setSectionDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nieuwe sectie</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Titel</Label>
                  <Input
                    value={sectionForm.title}
                    onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                    placeholder="bijv. Welkom, Werkregels, Veiligheid"
                  />
                </div>
                <Button onClick={handleCreateSection} className="w-full btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Sectie toevoegen
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Page Dialog */}
          <Dialog open={pageDialog} onOpenChange={setPageDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nieuwe pagina in "{selectedSection?.title}"</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Titel</Label>
                  <Input
                    value={pageForm.title}
                    onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })}
                    placeholder="bijv. Openingstijden, Kledingvoorschriften"
                  />
                </div>
                <Button onClick={handleCreatePage} className="w-full btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Pagina toevoegen
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Settings Dialog */}
          <Dialog open={handbookSettingsDialog} onOpenChange={setHandbookSettingsDialog}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Handboek instellingen</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pt-4">
                <div className="space-y-2">
                  <Label>Titel handboek</Label>
                  <Input
                    value={settingsForm.title}
                    onChange={(e) => setSettingsForm({ ...settingsForm, title: e.target.value })}
                    placeholder="Personeelshandboek"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtitel</Label>
                  <Input
                    value={settingsForm.subtitle}
                    onChange={(e) => setSettingsForm({ ...settingsForm, subtitle: e.target.value })}
                    placeholder="Welkom bij ons team!"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bedrijfsnaam</Label>
                  <Input
                    value={settingsForm.company_name}
                    onChange={(e) => setSettingsForm({ ...settingsForm, company_name: e.target.value })}
                    placeholder="Mijn Restaurant"
                  />
                </div>

                <div className="p-4 bg-amber-50 rounded-lg space-y-4">
                  <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Toegangsbeveiliging
                  </h4>
                  <div className="space-y-2">
                    <Label>Wachtwoord (optioneel)</Label>
                    <Input
                      type="text"
                      value={settingsForm.password}
                      onChange={(e) => setSettingsForm({ ...settingsForm, password: e.target.value })}
                      placeholder="Laat leeg voor openbare toegang"
                    />
                    <p className="text-xs text-amber-700">
                      Medewerkers moeten dit wachtwoord invoeren om het handboek te bekijken
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                  <div>
                    <Label className="text-base">Gepubliceerd</Label>
                    <p className="text-sm text-[#57534E]">
                      {settingsForm.is_published ? 'Handboek is zichtbaar' : 'Handboek is verborgen'}
                    </p>
                  </div>
                  <Switch
                    checked={settingsForm.is_published}
                    onCheckedChange={(checked) => setSettingsForm({ ...settingsForm, is_published: checked })}
                  />
                </div>

                {settingsForm.is_published && (
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <p className="text-sm text-emerald-700 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Publieke link: <code className="bg-white px-2 py-1 rounded">{window.location.origin}/handboek</code>
                    </p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={copyHandbookLink}>
                      <Copy className="w-4 h-4 mr-2" />
                      Link kopiëren
                    </Button>
                  </div>
                )}

                <Button onClick={handleSaveHandbookSettings} className="w-full btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  Instellingen opslaan
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

      {/* Employee Detail Dialog */}
      <Dialog open={employeeDetailDialog} onOpenChange={(open) => {
        setEmployeeDetailDialog(open);
        if (!open) setEditMode(false);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{editMode ? 'Medewerker bewerken' : 'Medewerker details'}</span>
              {!editMode && (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Bewerken
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmployee && !editMode && (
            <div className="space-y-6 pt-4">
              {/* Profile Header */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C2410C] to-[#D97706] flex items-center justify-center text-white font-bold text-2xl">
                  {selectedEmployee.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1C1917]">{selectedEmployee.name}</h3>
                  <p className="text-[#57534E]">{selectedEmployee.role}</p>
                  <Badge className={selectedEmployee.is_active ? 'bg-emerald-100 text-emerald-700 mt-1' : 'bg-stone-100 text-stone-600 mt-1'}>
                    {selectedEmployee.is_active ? 'Actief' : 'Inactief'}
                  </Badge>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-[#78716C] uppercase">Contactgegevens</h4>
                {selectedEmployee.email && (
                  <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <Mail className="w-5 h-5 text-[#C2410C]" />
                    <div>
                      <p className="text-xs text-[#78716C]">Email</p>
                      <p className="font-medium">{selectedEmployee.email}</p>
                    </div>
                  </div>
                )}
                {selectedEmployee.phone && (
                  <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <Phone className="w-5 h-5 text-[#C2410C]" />
                    <div>
                      <p className="text-xs text-[#78716C]">Telefoon</p>
                      <p className="font-medium">{selectedEmployee.phone}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Employment Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-[#78716C] uppercase">Dienstverband</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <Briefcase className="w-5 h-5 text-[#C2410C]" />
                    <div>
                      <p className="text-xs text-[#78716C]">Functie</p>
                      <p className="font-medium">{selectedEmployee.role || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <Euro className="w-5 h-5 text-[#C2410C]" />
                    <div>
                      <p className="text-xs text-[#78716C]">Uurloon</p>
                      <p className="font-medium">{formatCurrency(selectedEmployee.hourly_rate)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              {employeeData.find(e => e.id === selectedEmployee.id) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[#78716C] uppercase">Statistieken (30 dagen)</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 bg-emerald-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {employeeData.find(e => e.id === selectedEmployee.id)?.total_hours || 0}u
                      </p>
                      <p className="text-xs text-emerald-700">Gewerkt</p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-600">
                        {employeeData.find(e => e.id === selectedEmployee.id)?.overtime || 0}u
                      </p>
                      <p className="text-xs text-amber-700">Overuren</p>
                    </div>
                    <div className="p-3 bg-rose-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-rose-600">
                        {employeeData.find(e => e.id === selectedEmployee.id)?.sick_days || 0}
                      </p>
                      <p className="text-xs text-rose-700">Ziektedagen</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setEmployeeDetailDialog(false)}
                >
                  Sluiten
                </Button>
                <Button 
                  variant="outline" 
                  className="text-rose-600 hover:bg-rose-50"
                  onClick={() => handleDeleteEmployee(selectedEmployee.id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Verwijderen
                </Button>
              </div>
            </div>
          )}

          {selectedEmployee && editMode && (
            <form onSubmit={handleUpdateEmployee} className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Naam</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefoon</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Functie</Label>
                  <Input
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Uurloon (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.hourly_rate}
                    onChange={(e) => setEditForm({ ...editForm, hourly_rate: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editForm.is_active}
                  onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                />
                <Label>Actief</Label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setEditMode(false)}>
                  Annuleren
                </Button>
                <Button type="submit" className="flex-1 btn-primary">
                  Opslaan
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Availability Edit Dialog */}
      <Dialog open={availabilityDialog} onOpenChange={setAvailabilityDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Beschikbaarheid - {selectedAvailEmployee?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-[#57534E]">
              Stel in op welke dagen en tijden deze medewerker beschikbaar is.
            </p>
            
            <div className="space-y-3">
              {dayNames.map((day, idx) => {
                const dayData = (availabilityData[selectedAvailEmployee?.id] || []).find(a => a.day_of_week === idx) || {
                  is_available: true,
                  start_time: '09:00',
                  end_time: '17:00'
                };
                
                return (
                  <div 
                    key={day}
                    className={`p-3 rounded-lg border ${dayData.is_available ? 'border-emerald-200 bg-emerald-50' : 'border-stone-200 bg-stone-50'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={dayData.is_available}
                          onCheckedChange={(checked) => updateAvailabilityForDay(idx, 'is_available', checked)}
                        />
                        <span className="font-medium">{day}</span>
                      </div>
                      
                      {dayData.is_available && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={dayData.start_time}
                            onChange={(e) => updateAvailabilityForDay(idx, 'start_time', e.target.value)}
                            className="w-28 h-8"
                          />
                          <span className="text-stone-400">-</span>
                          <Input
                            type="time"
                            value={dayData.end_time}
                            onChange={(e) => updateAvailabilityForDay(idx, 'end_time', e.target.value)}
                            className="w-28 h-8"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setAvailabilityDialog(false)}>
                Annuleren
              </Button>
              <Button className="flex-1 btn-primary" onClick={handleSaveAvailability}>
                Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Schedule Dialog */}
      <Dialog open={quickScheduleDialog} onOpenChange={setQuickScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Dienst inplannen - {quickScheduleDate?.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSchedule} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Medewerker</Label>
              <Select
                value={scheduleForm.employee_id}
                onValueChange={(value) => setScheduleForm({ ...scheduleForm, employee_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer medewerker" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.is_active).map(emp => {
                    const dateStr = quickScheduleDate?.toISOString().split('T')[0];
                    const dayOfWeek = quickScheduleDate?.getDay();
                    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Mon=0
                    const status = dateStr ? getEmployeeStatus(emp.id, dateStr, adjustedDay) : 'beschikbaar';
                    
                    return (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center gap-2">
                          <span>{emp.name}</span>
                          <div className={`px-2 py-0.5 rounded text-[10px] ${statusColors[status]}`}>
                            {statusIcons[status]}
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Show availability hint */}
            {scheduleForm.employee_id && quickScheduleDate && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                {(() => {
                  const dateStr = quickScheduleDate.toISOString().split('T')[0];
                  const dayOfWeek = quickScheduleDate.getDay();
                  const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                  const status = getEmployeeStatus(scheduleForm.employee_id, dateStr, adjustedDay);
                  const empAvail = (availabilityData[scheduleForm.employee_id] || []).find(a => a.day_of_week === adjustedDay);
                  
                  if (status === 'vrij') {
                    return <span className="text-amber-700">⚠️ Deze medewerker heeft vrij op deze dag</span>;
                  } else if (status === 'niet_beschikbaar') {
                    return <span className="text-stone-600">⚠️ Deze medewerker is normaal niet beschikbaar op {dayNames[adjustedDay].toLowerCase()}</span>;
                  } else if (empAvail) {
                    return <span className="text-emerald-700">✓ Beschikbaar van {empAvail.start_time} tot {empAvail.end_time}</span>;
                  }
                  return <span className="text-blue-700">ℹ️ Geen beschikbaarheid ingesteld</span>;
                })()}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start</Label>
                <Input
                  type="time"
                  value={scheduleForm.shift_start}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, shift_start: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Eind</Label>
                <Input
                  type="time"
                  value={scheduleForm.shift_end}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, shift_end: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Locatie (optioneel)</Label>
              <Select
                value={scheduleForm.location_id || "none"}
                onValueChange={(value) => setScheduleForm({ ...scheduleForm, location_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer locatie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full btn-primary">
              Inplannen
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Optimization Dialog */}
      <Dialog open={optimizationDialog} onOpenChange={setOptimizationDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Rooster Optimalisatie
            </DialogTitle>
          </DialogHeader>
          
          {optimizationSuggestions && (
            <div className="space-y-6 pt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-purple-600">
                    {optimizationSuggestions.summary?.total_days || 0}
                  </p>
                  <p className="text-sm text-purple-700">Dagen geanalyseerd</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-emerald-600">
                    {optimizationSuggestions.summary?.fully_staffed_days || 0}
                  </p>
                  <p className="text-sm text-emerald-700">Volledig bezet</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-amber-600">
                    {optimizationSuggestions.summary?.understaffed_days || 0}
                  </p>
                  <p className="text-sm text-amber-700">Onderbezet</p>
                </div>
              </div>

              {/* Settings */}
              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="text-sm font-semibold mb-3">Bezetting instellingen</p>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Min. personeel:</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={optimizationSettings.minStaff}
                      onChange={(e) => setOptimizationSettings(prev => ({ ...prev, minStaff: parseInt(e.target.value) || 1 }))}
                      className="w-16 h-8"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Max. personeel:</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={optimizationSettings.maxStaff}
                      onChange={(e) => setOptimizationSettings(prev => ({ ...prev, maxStaff: parseInt(e.target.value) || 5 }))}
                      className="w-16 h-8"
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleOptimize}
                    disabled={optimizationLoading}
                  >
                    <Zap className="w-4 h-4 mr-1" />
                    Herbereken
                  </Button>
                </div>
              </div>

              {/* Daily Suggestions */}
              <div className="space-y-4">
                <p className="text-sm font-semibold">Suggesties per dag</p>
                {optimizationSuggestions.suggestions?.map((day) => (
                  <div 
                    key={day.date}
                    className={`p-4 rounded-lg border ${
                      day.is_understaffed 
                        ? 'border-amber-300 bg-amber-50' 
                        : 'border-emerald-300 bg-emerald-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          day.is_understaffed ? 'bg-amber-200' : 'bg-emerald-200'
                        }`}>
                          <Calendar className={`w-5 h-5 ${
                            day.is_understaffed ? 'text-amber-700' : 'text-emerald-700'
                          }`} />
                        </div>
                        <div>
                          <p className="font-semibold">{day.day_name}</p>
                          <p className="text-sm text-[#57534E]">
                            {new Date(day.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-[#57534E]">Nodig: <span className="font-semibold">{day.staff_needed}</span></p>
                          <p className="text-sm text-[#57534E]">
                            Gepland: <span className="font-semibold">{day.already_scheduled}</span>
                          </p>
                        </div>
                        {day.is_understaffed ? (
                          <Badge className="bg-amber-200 text-amber-800">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Onderbezet
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-200 text-emerald-800">
                            <UserCheck className="w-3 h-3 mr-1" />
                            Voldoende
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Already scheduled */}
                    {day.already_scheduled_employees?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-[#78716C] mb-2">Al ingepland:</p>
                        <div className="flex flex-wrap gap-2">
                          {day.already_scheduled_employees.map(emp => (
                            <div 
                              key={emp.id}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              {emp.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggestions */}
                    {day.suggested_employees?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-[#78716C] mb-2">Suggesties (klik om te selecteren):</p>
                        <div className="flex flex-wrap gap-2">
                          {(selectedSuggestions[day.date] || day.suggested_employees).map(emp => (
                            <button
                              key={emp.id}
                              onClick={() => toggleSuggestionEmployee(day.date, emp.id)}
                              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-all ${
                                emp.selected
                                  ? 'bg-purple-200 text-purple-800 border-2 border-purple-400'
                                  : 'bg-stone-100 text-stone-500 border-2 border-stone-200'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                emp.selected ? 'border-purple-600 bg-purple-600' : 'border-stone-300'
                              }`}>
                                {emp.selected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className="font-medium">{emp.name}</span>
                              <span className="text-xs opacity-70">({emp.role})</span>
                              <span className="text-xs">{emp.suggested_start}-{emp.suggested_end}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {day.suggested_employees?.length === 0 && day.already_scheduled === 0 && (
                      <p className="text-sm text-amber-700">
                        Geen beschikbare medewerkers voor deze dag
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" className="flex-1" onClick={() => setOptimizationDialog(false)}>
                  Annuleren
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleApplySelectedSuggestions}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Geselecteerde diensten inplannen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersoneelPlanningPage;
